import dynamic from 'next/dynamic';

import { MouseEvent, ReactNode } from 'react';
import { isDesktop } from 'react-device-detect';
import { toast } from 'react-toastify';

import classNames from 'classnames';
import Cookies from 'js-cookie';

import { ProgressBar, Spinner, useModalController } from '@uikit';

import {
  useAppCurrentUserMetaQuery,
  useCheckMagicIDNecessaryDataQuery,
  useFeatureTogglesQuery,
} from '@query';

import { BrandColorSnapshot } from '@uikit/icons/BrandColorSnapshot';

import { AppValidation, DeserializedAppData, INTERNAL_VOTING_TYPE } from '@shared/api/types';
import { useAuth } from '@shared/common/providers/AuthProvider';
import {
  useAuthModal,
  useCompleteMagicIdModal,
} from '@shared/common/providers/GlobalModalsProvider';
import { storageKeys } from '@shared/constants';
import displayWalletErrorToast from '@shared/helpers/displayWalletErrorToast';
import { getSnapshotProposalUrl } from '@shared/helpers/snapshot';
import clearSavedConnectionDetails from '@shared/helpers/web3/clearSavedConnectionDetails';
import useWeb3 from '@shared/helpers/web3/hooks/useWeb3';

import { useSnapshotStoredVote } from '@store:web/utils/hooks/useSnapshotStoredVote';

const VotingModal = dynamic(() => import('./VotingModal'), { ssr: false });

interface AppValidationVotingProps {
  title?: ReactNode;
  validation: AppValidation;
  app: DeserializedAppData;
  className?: string;
  titleTag?: 'h2' | 'h3' | 'span';
}

const AppVotingButtonText = ({ isLoading, isVoted }: { isLoading: boolean; isVoted: boolean }) => {
  return (
    <>
      {isLoading && <Spinner color="light" size="sm" />}
      {!isLoading && (isVoted ? 'Voted' : 'Vote')}
    </>
  );
};

const AppValidationVoting = ({
  title = 'Current Results:',
  titleTag: TitleTag = 'span',
  validation,
  className,
  app,
}: AppValidationVotingProps) => {
  const { isAuth, isLoading: isAuthLoading, user } = useAuth();
  const { connectWallet, isLoading: isConnectingToWallet } = useWeb3();
  const voteModal = useModalController();
  const { data: selfAppMeta, isPending: isSelfAppMetaLoading } = useAppCurrentUserMetaQuery(
    app.attributes.appLink,
    {
      enabled: isAuth,
    },
  );
  const storedSnapshotVote = useSnapshotStoredVote({
    appId: app.attributes.appLink,
    proposalId: validation.id,
  });
  const { data: featureToggles = {} } = useFeatureTogglesQuery();
  const isVoted = !!selfAppMeta?.attributes.voted || !!storedSnapshotVote;
  const { data: missingFieldsEntry, isPending: isCheckingFields } =
    useCheckMagicIDNecessaryDataQuery(
      {
        name: 1,
        email: 1,
        'wallets[]': 'EVM',
      },
      { enabled: isAuth },
    );
  const isLoading = isAuthLoading || (isAuth && isSelfAppMetaLoading);
  const isVoteButtonDisabled =
    isConnectingToWallet || (isAuth && isCheckingFields) || isVoted || isLoading;
  const progresses = Object.entries(validation.result || {}).map(([label, value]) => ({
    label,
    value,
  }));
  const authModal = useAuthModal();
  const completeMagicIdModal = useCompleteMagicIdModal();

  const handleVoteButtonClick = async () => {
    // 1. Check if user is authenticated
    if (!isAuth) {
      authModal.open();
      return;
    }

    if (isCheckingFields || isConnectingToWallet) {
      return;
    }

    if (isVoted) {
      const blockReason = `You have already voted`;
      toast(blockReason, { toastId: blockReason });
      return;
    }

    // 2. Check if the user is eligible to vote
    if (!missingFieldsEntry?.success) {
      completeMagicIdModal.open(missingFieldsEntry);
      return;
    }

    // 3. Check if Trust Wallet is connected and disallow voting
    const userEVMWallet = user?.wallets?.find((wallet) => wallet.attributes.network === 'EVM');
    const evm = await import('@shared/helpers/web3/evm').then((module) => module.default);
    const isTrustWalletConnected = userEVMWallet && evm.isTrustWalletExtensionActive();

    if (isTrustWalletConnected) {
      const blockReason = `Voting is not supported through the Trust Wallet browser extension. Please connect a different EVM-compatible wallet to your MagicID account.`;
      toast.error(blockReason, { toastId: blockReason });
      return;
    }

    /*
        4. Check if internal validation for voting is enabled and only allowed on desktop/laptop platforms
        If it is true then DO NOT open the link and handle it internally
      */

    const checkIsDesktop = () =>
      validation?.voting === INTERNAL_VOTING_TYPE.MAGICSTORE ? true : isDesktop;

    if (featureToggles.internalValidationVote && checkIsDesktop()) {
      // 4.1 Trigger connection request to ensure the user is connected to their EVM account in MagicID
      if (userEVMWallet) {
        if (validation?.voting === INTERNAL_VOTING_TYPE.MAGICSTORE) {
          voteModal.open();
          return;
        }

        try {
          clearSavedConnectionDetails();
          Cookies.set(storageKeys.PREVENT_LOG_OUT_ON_ACCOUNT_CHANGE, 'true');

          const address = await connectWallet({
            walletType: 'EVM',
            targetAddress: userEVMWallet.attributes.address,
            hideToasts: true,
          });

          Cookies.remove(storageKeys.PREVENT_LOG_OUT_ON_ACCOUNT_CHANGE);

          // 4.2 [LAST SUCCESS STEP] Show the vote modal if the connection is successful
          if (address) {
            voteModal.open();
          }
        } catch (error) {
          toast.dismiss();
          displayWalletErrorToast({ error });
        }
      }
    }
  };

  const commonButtonProps = {
    className: classNames('button button-solid button-color-green button-xl', {
      'opacity-40': isVoteButtonDisabled,
      'pointer-events-none': isLoading,
    }),
    'data-qa': 'vote-button',
  };

  const handleSeeResultsLinkClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!isAuth) {
      authModal.open();
      event.preventDefault();
    }
  };

  return (
    <div className={classNames('flex flex-col gap-4', className)}>
      <div className="border border-primary-200 rounded-lg p-8">
        <TitleTag className="t-section-title mb-4 text-primary">{title}</TitleTag>

        <ul className="flex flex-col gap-4">
          {progresses.map(({ label, value }) => {
            return (
              <li key={label}>
                <p className="mb-2 flex justify-between capitalize" data-qa="validation-result">
                  {label}
                  <span data-qa="validation-result-value">{value}%</span>
                </p>
                <ProgressBar value={value} progressClassName="bg-secondary-700" />
              </li>
            );
          })}
        </ul>
      </div>
      {!validation.finished && validation.voting === INTERNAL_VOTING_TYPE.MAGICSTORE && (
        // self vote logic
        <button onClick={handleVoteButtonClick} {...commonButtonProps}>
          <AppVotingButtonText isLoading={isLoading} isVoted={isVoted} />
        </button>
      )}

      {!validation.finished && validation.voting !== INTERNAL_VOTING_TYPE.MAGICSTORE && (
        // Open the snapshot proposal page
        <a
          href={getSnapshotProposalUrl(validation.id)}
          target="_blank"
          rel="noreferrer"
          {...commonButtonProps}
        >
          <AppVotingButtonText isLoading={isLoading} isVoted={isVoted} />
        </a>
      )}

      {validation.voting === INTERNAL_VOTING_TYPE.SNAPSHOT && (
        <a
          href={getSnapshotProposalUrl(validation.id)}
          target="_blank"
          rel="noreferrer"
          className="button button-lg relative bg-neutral-700 text-neutral-50 group gap-1"
          onClick={handleSeeResultsLinkClick}
          data-qa="vote-button"
        >
          <BrandColorSnapshot className="icon sm:my-auto sm:absolute sm:left-9 sm:top-0 sm:bottom-0" />
          See results in Snapshot
          <BrandColorSnapshot className="icon-lg opacity-30 absolute right-0 bottom-0 sm:top-0 sm:my-auto sm:right-6" />
          <div className="w-full h-full absolute pointer-events-none left-0 top-0 translate-x-[5px] translate-y-[3px] border border-neutral-700 rounded-full group-hover:translate-x-0 group-hover:translate-y-0 transition-transform" />
        </a>
      )}

      {featureToggles.internalValidationVote && (
        <VotingModal
          app={app}
          validationId={validation.id}
          strategy={validation.voting}
          {...voteModal.props}
        />
      )}
    </div>
  );
};

export default AppValidationVoting;
